import { Form, Switch } from "antd";
import { useForm } from "antd/es/form/Form";
import {
  ResetDomain,
  useEmployeeManagementResetNotice,
} from "components/modals/EmployeeManagementResetNoticeModal";
import { useReactivationPreview } from "components/modals/EmployeeManagementReactivationWarningModal";
import { useBusinessSettings } from "hooks/queries/business-settings";
import PolicySettingsPage, {
  DiffRow,
  fmtBool,
} from "pages/settings/policy/PolicySettingsPage";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { useEffect } from "react";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";
import { AffectedInstructorOccurrence, UpdateBusinessPoliciesForm } from "shared/types";

type Values = {
  employee_management_enabled: boolean;
  employee_management_working_hours_enabled: boolean;
  employee_management_leave_enabled: boolean;
  employee_management_class_assignment_enabled: boolean;
};

// The master toggle reveals one sub-toggle per instructor-profile tab
// (Jam Kerja / Cuti / Kelas yang Ditugaskan), each independently gating
// whether its tab appears on InstructorDetails AND whether its backend
// enforcement runs (see assertInstructorAvailable, schedule_v2.ts). Instructor
// double-booking prevention is a separate, independent toggle — see
// ScheduleConflictPolicyPage — since it needs no data from this feature.
const EmployeeManagementPage = () => {
  const [form] = useForm<Values>();
  const enabled = Form.useWatch("employee_management_enabled", form);
  // PolicySettingsPage hydrates via setFieldsValue (not AntD's native
  // initialValues), so form.resetFields() can't recover the server's actual
  // sub-toggle values. This reads the same cached query directly instead.
  const { data: settings } = useBusinessSettings();
  // Leave still uses the business-wide reactivation scan — it's the only
  // one of the three where old real data can silently reactivate on its
  // own (nothing archives/resets it). Working hours and class assignment
  // moved to a different, deterministic flow: see resetDomains below and
  // updateBusinessPolicies (business.ts) for where the actual
  // archive-and-reset happens.
  const { checkAndConfirm, reactivationPreviewWarning, isChecking } = useReactivationPreview();
  const { confirmAndProceed, resetNoticeModal } = useEmployeeManagementResetNotice();

  // Only "effective enforcement" transitions matter — a sub-flag flipping on
  // while the master was already on counts the same as the master flipping
  // on while the sub-flag was already on; either way that check type just
  // went from not-enforced to enforced. Raw flag diffs alone (pushIfChanged
  // below) would miss the master-alone and sub-flag-alone cases.
  const beforeConfirm = (payload: UpdateBusinessPoliciesForm, proceed: () => void) => {
    if (!settings) {
      proceed();
      return;
    }
    const oldLeave = settings.employee_management_enabled && settings.employee_management_leave_enabled;
    const newLeave = payload.employee_management_enabled && payload.employee_management_leave_enabled;
    const oldWorkingHours =
      settings.employee_management_enabled && settings.employee_management_working_hours_enabled;
    const newWorkingHours =
      payload.employee_management_enabled && payload.employee_management_working_hours_enabled;
    const oldClassAssignment =
      settings.employee_management_enabled && settings.employee_management_class_assignment_enabled;
    const newClassAssignment =
      payload.employee_management_enabled && payload.employee_management_class_assignment_enabled;

    const relevantReasons = new Set<AffectedInstructorOccurrence["reasons"][number]>();
    if (newLeave && !oldLeave) relevantReasons.add("leave");

    const resetDomains = new Set<ResetDomain>();
    if (newWorkingHours && !oldWorkingHours) resetDomains.add("working_hours");
    if (newClassAssignment && !oldClassAssignment) resetDomains.add("class_assignment");

    if (relevantReasons.size === 0 && resetDomains.size === 0) {
      proceed();
      return;
    }
    // "Batal" abandons the save entirely — nothing was ever sent to the
    // server — but the form itself still shows whatever the admin just
    // flipped on, since AntD doesn't know this save attempt got cancelled.
    // Put back exactly the toggle(s) that changed as part of THIS attempt
    // (master and/or any sub-flag, whichever actually differ from the
    // last-saved settings), not a blanket reset — an unrelated edit sitting
    // in the same form (e.g. this save also happened to include some other
    // future field) should survive a "Batal" on this specific warning.
    const revertToggledFields: Partial<Values> = {};
    if (payload.employee_management_enabled !== settings.employee_management_enabled) {
      revertToggledFields.employee_management_enabled = settings.employee_management_enabled;
    }
    if (
      payload.employee_management_working_hours_enabled !==
      settings.employee_management_working_hours_enabled
    ) {
      revertToggledFields.employee_management_working_hours_enabled =
        settings.employee_management_working_hours_enabled;
    }
    if (payload.employee_management_leave_enabled !== settings.employee_management_leave_enabled) {
      revertToggledFields.employee_management_leave_enabled =
        settings.employee_management_leave_enabled;
    }
    if (
      payload.employee_management_class_assignment_enabled !==
      settings.employee_management_class_assignment_enabled
    ) {
      revertToggledFields.employee_management_class_assignment_enabled =
        settings.employee_management_class_assignment_enabled;
    }
    const revert = () => form.setFieldsValue(revertToggledFields);

    // Chain: the reset-notice gate (working hours / class assignment) runs
    // first, then the leave reactivation-scan gate, then the outer proceed.
    // Either gate can be a no-op (confirmAndProceed calls its onConfirm
    // immediately when resetDomains is empty; the leave check is skipped
    // entirely, not just filtered to empty, when relevantReasons is empty —
    // avoids an unnecessary network call on a pure reset-only save).
    confirmAndProceed(
      resetDomains,
      () => {
        if (relevantReasons.size > 0) {
          checkAndConfirm(relevantReasons, proceed, revert);
        } else {
          proceed();
        }
      },
      revert,
    );
  };

  // The sub-toggle Form.Items below unmount while the master is off, but
  // AntD's default preserve:true keeps whatever value they last held in the
  // form store. Without this, editing a sub-toggle and then turning the
  // master off before saving would still silently include that edit in the
  // save payload (and the confirm diff) even though it's hidden and appears
  // irrelevant. Functionally inert today (the backend gates on master &&
  // sub-flag everywhere), but a confusing diff, and it re-arms the moment
  // the master is re-enabled later with no visible trail. Re-syncing hidden
  // fields back to the server's own last-saved values, discarding any
  // pending edit to them, matches what hiding them implies.
  useEffect(() => {
    if (enabled || !settings) return;
    form.setFieldsValue({
      employee_management_working_hours_enabled: settings.employee_management_working_hours_enabled,
      employee_management_leave_enabled: settings.employee_management_leave_enabled,
      employee_management_class_assignment_enabled: settings.employee_management_class_assignment_enabled,
    });
  }, [enabled, settings, form]);

  // Built at render time, never module scope: translate() is bare i18next.t
  // and resolves before i18n init.
  const subToggles: { name: keyof Values; label: string; desc: string }[] = [
    {
      name: "employee_management_working_hours_enabled",
      label: translate("tabs.working_hours"),
      desc: translate("class_settings.employee_management.working_hours_desc"),
    },
    {
      name: "employee_management_leave_enabled",
      label: translate("tabs.time_off"),
      desc: translate("class_settings.employee_management.leave_desc"),
    },
    {
      name: "employee_management_class_assignment_enabled",
      label: translate("tabs.assigned_classes"),
      desc: translate("class_settings.employee_management.class_assignment_desc"),
    },
  ];

  return (
    <>
      {resetNoticeModal}
      {reactivationPreviewWarning}
      <PolicySettingsPage<Values>
        sectioned
        capability={AdminCapability.OperationalSettings}
        title={translate("class_settings.employee_management.title")}
        confirmNote={translate("class_settings.confirm.note_employee_management")}
        beforeConfirm={beforeConfirm}
        beforeConfirmLoading={isChecking}
        form={form}
        initialValues={(s) => ({
          employee_management_enabled: s.employee_management_enabled,
          employee_management_working_hours_enabled: s.employee_management_working_hours_enabled,
          employee_management_leave_enabled: s.employee_management_leave_enabled,
          employee_management_class_assignment_enabled: s.employee_management_class_assignment_enabled,
        })}
        buildSave={(v, s) => {
          const rows: DiffRow[] = [];
          const pushIfChanged = (name: keyof Values, label: string) => {
            const next = !!v[name];
            if (next !== s[name]) {
              rows.push({ label, before: fmtBool(s[name]), after: fmtBool(next) });
            }
          };
          pushIfChanged("employee_management_enabled", translate("class_settings.employee_management.toggle"));
          subToggles.forEach(({ name, label }) => pushIfChanged(name, label));

          return {
            rows,
            payload: {
              employee_management_enabled: !!v.employee_management_enabled,
              employee_management_working_hours_enabled: !!v.employee_management_working_hours_enabled,
              employee_management_leave_enabled: !!v.employee_management_leave_enabled,
              employee_management_class_assignment_enabled: !!v.employee_management_class_assignment_enabled,
            },
          };
        }}
      >
        <SettingsSection title={translate("class_settings.employee_management.section_status")}>
          <SettingRow
            last
            title={translate("class_settings.employee_management.toggle")}
            control={
              <Form.Item name="employee_management_enabled" valuePropName="checked" noStyle>
                <Switch aria-label={translate("class_settings.employee_management.toggle")} />
              </Form.Item>
            }
          />
        </SettingsSection>

        {enabled && (
          <SettingsSection title={translate("class_settings.employee_management.section_rules")}>
            {subToggles.map(({ name, label, desc }, idx) => (
              <SettingRow
                key={name}
                last={idx === subToggles.length - 1}
                title={label}
                hint={desc}
                control={
                  <Form.Item name={name} valuePropName="checked" noStyle>
                    <Switch aria-label={label} />
                  </Form.Item>
                }
              />
            ))}
          </SettingsSection>
        )}
      </PolicySettingsPage>
    </>
  );
};

export default EmployeeManagementPage;
